import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  Box,
  Typography,
  PanelCard,
  Alert,
  Spinner,
  Chip,
  TextField,
  Select,
  Button,
  Avatar,
  GLPGrid,
} from '@glplus/glpweb-dsl';
import { useVendor } from '../lib/CapabilitiesContext';
import { vendorApiClient, type TeamMember, type VendorRole, type VendorProfile } from '../lib/vendorApiClient';

/**
 * Business (approved IA) — business information and the team roster
 * (owner/manager/clerk). Team management
 * (invite / remove) is owner-only (team:manage); the API enforces the
 * last-owner guard, which we surface.
 */

const NAVY = '#343F5B';
const GREEN = '#25862D';
const ORANGE = '#F67A1A';

function formatAddress(v: unknown): string {
  if (!v || typeof v !== 'object') return '';
  const a = v as Record<string, unknown>;
  return [a.line1, a.line2, a.city, a.state, a.postalCode].filter(Boolean).join(', ');
}

export default function Team() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeVendorId, activeMembership, data, has } = useVendor();
  const canManage = has('team:manage');

  const [profile, setProfile] = useState<VendorProfile | null>(null);
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<VendorRole>('clerk');
  const [busy, setBusy] = useState(false);
  const inviteReady = email.trim().length > 0 && firstName.trim().length > 0 && lastName.trim().length > 0;

  const load = useCallback(
    (vendorId: string) => {
      setError(null);
      vendorApiClient.getProfile(vendorId).then(setProfile).catch((e: Error) => setError(e.message));
      if (canManage) {
        setMembers(null);
        vendorApiClient
          .listTeam(vendorId)
          .then((r) => setMembers(r.items))
          .catch((e: Error) => setError(e.message));
      }
    },
    [canManage],
  );

  useEffect(() => {
    if (activeVendorId) load(activeVendorId);
  }, [activeVendorId, load]);

  const addMember = async () => {
    if (!activeVendorId || !inviteReady) return;
    setBusy(true);
    setError(null);
    try {
      // Provisions the teammate's vendor-pool account; Cognito emails the invitation.
      await vendorApiClient.addMember(activeVendorId, {
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
      });
      setEmail('');
      setFirstName('');
      setLastName('');
      setRole('clerk');
      setInviting(false);
      load(activeVendorId);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (membershipId: string) => {
    if (!activeVendorId) return;
    setBusy(true);
    setError(null);
    try {
      await vendorApiClient.removeMember(activeVendorId, membershipId);
      load(activeVendorId);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (membershipId: string, nextRole: VendorRole) => {
    if (!activeVendorId || nextRole === 'owner') return;
    setBusy(true); setError(null);
    try { await vendorApiClient.updateMember(activeVendorId, membershipId, { role: nextRole }); load(activeVendorId); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  if (!activeVendorId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  const roleOptions = [
    { value: 'manager', label: t('team.roleManager') },
    { value: 'clerk', label: t('team.roleClerk') },
  ];
  const roleLabel = (r: VendorRole) => t(`team.role${r.charAt(0).toUpperCase() + r.slice(1)}`);
  const roleChipColor = (r: VendorRole): { bg: string; fg: string } =>
    r === 'owner' ? { bg: '#FFF1E3', fg: '#C2410C' } : r === 'manager' ? { bg: '#E3F4E7', fg: '#1B7A33' } : { bg: '#EEF1F8', fg: '#343F5B' };

  const posSystem = profile?.posSystem ?? '';
  const subtitle = [activeMembership?.businessName, activeMembership?.glpVendorId].filter(Boolean).join(' · ');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1040 }}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {t('team.businessTitle')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.25 }}>
          {subtitle}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Business information */}
      <PanelCard
        accent={NAVY}
        title={t('team.businessInfo', { defaultValue: 'Business information' })}
        sx={{ height: 'auto', bgcolor: '#EEF1F8' }}
        actions={
          <Button
            ariaLabel={t('team.editPublicProfile')}
            buttonText={t('team.editPublicProfile')}
            variant="outlinedOrange"
            size="small"
            onClick={() => navigate('/business/profile')}
          />
        }
      >
        <GLPGrid container spacing={2}>
          <GLPGrid size={{ xs: 12, md: 6 }}>
            <TextField label={t('team.legalEntity', { defaultValue: 'Legal entity' })} ariaLabel={t('team.legalEntity', { defaultValue: 'Legal entity' })} value={profile?.businessName ?? ''} onChange={() => {}} disabled />
          </GLPGrid>
          <GLPGrid size={{ xs: 12, md: 6 }}>
            <TextField label={t('team.phone', { defaultValue: 'Phone' })} ariaLabel={t('team.phone', { defaultValue: 'Phone' })} value="" placeholder={t('team.notSet', { defaultValue: 'Not set' })} onChange={() => {}} disabled />
          </GLPGrid>
          <GLPGrid size={{ xs: 12, md: 6 }}>
            <TextField label={t('team.contactEmail', { defaultValue: 'Contact email' })} ariaLabel={t('team.contactEmail', { defaultValue: 'Contact email' })} value={data?.email ?? ''} onChange={() => {}} disabled />
          </GLPGrid>
          <GLPGrid size={{ xs: 12, md: 6 }}>
            <TextField label={t('team.address', { defaultValue: 'Address' })} ariaLabel={t('team.address', { defaultValue: 'Address' })} value={formatAddress(profile?.address)} placeholder={t('team.notSet', { defaultValue: 'Not set' })} onChange={() => {}} disabled />
          </GLPGrid>
        </GLPGrid>
      </PanelCard>

      {/* Team */}
      <PanelCard
        accent={GREEN}
        title={t('team.members', { defaultValue: 'Team' })}
        subtitle={t('team.membersHelp', { defaultValue: 'Owners, managers and clerks for this vendor.' })}
        sx={{ height: 'auto', bgcolor: '#F3F8F3' }}
        actions={
          canManage ? (
            <Box
              component="button"
              type="button"
              onClick={() => setInviting((v) => !v)}
              sx={{ border: 'none', background: 'transparent', color: GREEN, fontWeight: 700, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {t('team.invite', { defaultValue: '+ Invite' })}
            </Box>
          ) : undefined
        }
      >
        {!canManage ? (
          <Alert severity="info">{t('team.ownerOnly')}</Alert>
        ) : members === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <Spinner color="brand" />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {inviting && (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap', pb: 2, mb: 1, borderBottom: '1px solid #e2ebe4' }}>
                <Box sx={{ flex: '1 1 140px' }}>
                  <TextField label={t('team.firstName')} ariaLabel={t('team.firstName')} value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={busy} />
                </Box>
                <Box sx={{ flex: '1 1 140px' }}>
                  <TextField label={t('team.lastName')} ariaLabel={t('team.lastName')} value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={busy} />
                </Box>
                <Box sx={{ flex: '1 1 220px' }}>
                  <TextField label={t('team.email')} ariaLabel={t('team.email')} type="email" placeholder={t('team.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
                </Box>
                <Box sx={{ minWidth: 150 }}>
                  <Select ariaLabel={t('team.role')} label={t('team.role')} value={role} onChange={(_e, v) => setRole(v as VendorRole)} options={roleOptions} />
                </Box>
                <Button ariaLabel={t('team.invite')} buttonText={t('team.invite')} variant="contained" onClick={addMember} disabled={busy || !inviteReady} />
                <Typography sx={{ flexBasis: '100%', fontSize: 12, color: 'text.secondary' }}>{t('team.inviteHint')}</Typography>
              </Box>
            )}
            {members.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('team.noMembers')}
              </Typography>
            ) : (
              members.map((m, i) => {
                const c = roleChipColor(m.role);
                return (
                  <Box key={m.membershipId} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderTop: i > 0 ? '1px solid #e2ebe4' : 'none' }}>
                    <Avatar ariaLabel={m.email ?? ''} fallbackText={m.givenName && m.familyName ? `${m.givenName} ${m.familyName}` : (m.email ?? '?')} bgColor="#cdd5e0" size="small" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.givenName || m.familyName ? `${m.givenName ?? ''} ${m.familyName ?? ''}`.trim() : m.email}
                      </Typography>
                      {(m.givenName || m.familyName) && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</Typography>
                      )}
                    </Box>
                    {m.status === 'invited' && (
                      <Chip label={t('team.statusInvited')} size="small" backgroundColor="#FEF1E7" textColor="#A85200" fontSize={11} />
                    )}
                    {m.role === 'owner' ? <Chip label={roleLabel(m.role)} size="small" backgroundColor={c.bg} textColor={c.fg} fontSize={11} /> : <Box sx={{ minWidth: 135 }}><Select ariaLabel={t('team.changeRole', { email: m.email ?? '' })} value={m.role} onChange={(_e, value) => void changeRole(m.membershipId, value as VendorRole)} options={roleOptions} size="small" disabled={busy} /></Box>}
                    <Button
                      ariaLabel={t('team.removeAria', { email: m.email ?? '' })}
                      buttonText={t('common.remove')}
                      variant="outlinedOrange"
                      size="small"
                      onClick={() => removeMember(m.membershipId)}
                      disabled={busy || m.role === 'owner'}
                    />
                  </Box>
                );
              })
            )}
          </Box>
        )}
      </PanelCard>

      {/* Point of sale */}
      <PanelCard accent={ORANGE} title={t('team.pos', { defaultValue: 'Point of sale' })} sx={{ height: 'auto', bgcolor: '#FFF6EE' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: posSystem ? '#1B7A33' : '#9aa3b0', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>{posSystem || t('team.noPos', { defaultValue: 'No POS connected' })}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {posSystem ? t('team.posConnected', { defaultValue: 'Connected' }) : t('team.posConnectHelp', { defaultValue: 'Connect a POS to sync your catalog.' })}
              </Typography>
            </Box>
          </Box>
          <Box component="span" sx={{ color: '#9aa3b0', fontWeight: 700, fontSize: '14px' }}>
            {t('team.reconnect', { defaultValue: 'Reconnect' })}
          </Box>
        </Box>
      </PanelCard>

    </Box>
  );
}
